export interface GeocodingResult {
  display_name: string;
  lat: string;
  lon: string;
  place_id: number;
  importance: number;
  type: string;
  class: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    country?: string;
    country_code?: string;
  };
}

export interface GeocodingResponse {
  results: GeocodingResult[];
  query: string;
}

export interface ReverseGeocodingResult {
  city: string;
  country: string;
  display_name: string;
}

// Mock data for demonstration purposes when external API is blocked
const mockSearchResults: Record<string, GeocodingResult[]> = {
  'new york': [
    {
      display_name: 'New York, New York, United States',
      lat: '40.7128',
      lon: '-74.0060',
      place_id: 1,
      importance: 0.9,
      type: 'city',
      class: 'place'
    },
    {
      display_name: 'New York State, United States',
      lat: '43.0000',
      lon: '-75.0000',
      place_id: 2,
      importance: 0.8,
      type: 'state',
      class: 'place'
    }
  ],
  'paris': [
    {
      display_name: 'Paris, France',
      lat: '48.8566',
      lon: '2.3522',
      place_id: 3,
      importance: 0.9,
      type: 'city',
      class: 'place'
    }
  ],
  'london': [
    {
      display_name: 'London, England, United Kingdom',
      lat: '51.5074',
      lon: '-0.1278',
      place_id: 4,
      importance: 0.9,
      type: 'city',
      class: 'place'
    }
  ],
  'tokyo': [
    {
      display_name: 'Tokyo, Japan',
      lat: '35.6762',
      lon: '139.6503',
      place_id: 5,
      importance: 0.9,
      type: 'city',
      class: 'place'
    }
  ],
  'san francisco': [
    {
      display_name: 'San Francisco, California, United States',
      lat: '37.7749',
      lon: '-122.4194',
      place_id: 6,
      importance: 0.9,
      type: 'city',
      class: 'place'
    }
  ]
};

class GeocodingService {
  private baseUrl = 'https://nominatim.openstreetmap.org/search';

  async searchAddress(query: string): Promise<GeocodingResponse> {
    if (!query.trim()) {
      return { results: [], query };
    }

    // First try the real API
    try {
      const params = new URLSearchParams({
        q: query,
        format: 'json',
        limit: '5',
        addressdetails: '1',
        'accept-language': 'en'
      });

      const response = await fetch(`${this.baseUrl}?${params}`, {
        headers: {
          'User-Agent': 'Blip/1.0 (Contact: support@blip-platform.com)'
        }
      });

      if (!response.ok) {
        throw new Error(`Geocoding request failed: ${response.status}`);
      }

      const results: GeocodingResult[] = await response.json();
      
      return {
        results: results.slice(0, 5), // Limit to 5 results
        query
      };
    } catch (error) {
      console.warn('External geocoding API not available, using mock data:', error);
      
      // Fallback to mock data for demonstration
      const normalizedQuery = query.toLowerCase().trim();
      const matchingResults: GeocodingResult[] = [];
      
      // Search through mock data for partial matches
      Object.keys(mockSearchResults).forEach(key => {
        if (normalizedQuery.includes(key) || key.includes(normalizedQuery)) {
          matchingResults.push(...mockSearchResults[key]);
        }
      });

      return {
        results: matchingResults.slice(0, 5),
        query
      };
    }
  }

  // Debounce utility for search input
  debounce<T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  // Reverse geocode coordinates to get city and country
  async reverseGeocode(lat: number, lon: number): Promise<ReverseGeocodingResult> {
    try {
      const params = new URLSearchParams({
        lat: lat.toString(),
        lon: lon.toString(),
        format: 'json',
        addressdetails: '1',
        'accept-language': 'en'
      });

      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`, {
        headers: {
          'User-Agent': 'Blip/1.0 (Contact: support@blip-platform.com)'
        }
      });

      if (!response.ok) {
        throw new Error(`Reverse geocoding request failed: ${response.status}`);
      }

      const data: any = await response.json();
      
      // Extract city name (can be in various fields)
      const city = data.address?.city || 
                   data.address?.town || 
                   data.address?.village || 
                   data.address?.municipality || 
                   data.address?.county || 
                   data.address?.state || 
                   '';
      
      const country = data.address?.country || '';
      
      return {
        city,
        country,
        display_name: data.display_name || ''
      };
    } catch (error) {
      console.warn('Reverse geocoding failed, using fallback:', error);
      
      // Return empty values as fallback
      return {
        city: '',
        country: '',
        display_name: ''
      };
    }
  }
}

export const geocodingService = new GeocodingService();